/**
 * A client's own report emails (batch 6, F07; docs/specs/cloud/app-email.md; help `cloud.security`): on My sign-in,
 * whether they are emailed when the organisation publishes a daily or weekly report to their organisation's portal.
 * On by default; their own choice only (RLS: own rows).
 */
import { useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { appEmailSenderName } from '@teslyn/domain';
import { CheckField } from '../components/Fields.tsx';
import { loadReportEmailChoice, saveReportEmailChoice } from './api.ts';

export interface ReportEmailsChoiceProps {
  readonly sb: SupabaseClient;
  readonly userId: string;
  readonly clientOrgId: string;
  readonly clientOrgName: string;
  /** The organisation that publishes the reports (the tenant). */
  readonly tenantName: string;
}

export function ReportEmailsChoice({
  sb,
  userId,
  clientOrgId,
  clientOrgName,
  tenantName,
}: ReportEmailsChoiceProps): ReactElement {
  const [on, setOn] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadReportEmailChoice(sb, clientOrgId)
      .then((v) => live && setOn(v))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [sb, clientOrgId]);
  return (
    <div className="panel stack" aria-labelledby="report-emails">
      <h2 className="heading" id="report-emails">
        Report emails
      </h2>
      <p className="body">
        {tenantName} can email you when it publishes a daily or weekly report for {clientOrgName}, with a link
        to Reports, the PDF or both. The emails come from &quot;{appEmailSenderName(tenantName)}&quot;; a
        reply reaches whoever published the report. The same choice, with push on this device and the other
        notices, is on the Notices page.
      </p>
      {on !== null && (
        <CheckField
          label="Email me when a report is published"
          checked={on}
          onChange={(v) => {
            setOn(v);
            setSaved(null);
            setError(null);
            saveReportEmailChoice(sb, userId, clientOrgId, v)
              .then(() =>
                setSaved(
                  v ? 'You will be emailed about new reports.' : 'You will not be emailed about new reports.',
                ),
              )
              .catch((e: unknown) => {
                setOn(!v);
                setError(e instanceof Error ? e.message : String(e));
              });
          }}
        />
      )}
      {saved && (
        <p className="caption" role="status">
          {saved}
        </p>
      )}
      {error && (
        <p className="notice notice--fail" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
